import type {
  BalanceResponse,
  ClientAllowance,
  OpenHold,
  PlayerBucket,
} from "@pax/api-contract/accounting/v1alpha1";
import {
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  HapticSurface,
  panelRow,
  panelRowQuiet,
  Skeleton,
} from "@pax/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Flag } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { creditsFromRaw, formatCredits } from "@/features/credits/format";
import { journalLabel, readableCode } from "@/features/credits/journals";
import {
  balanceQuery,
  type CreditHistoryLine,
  creditHistoryQuery,
  mySubscriptionQuery,
  openHoldsQuery,
  spendableCredits,
} from "@/features/credits/queries";
import { AgentFeedback } from "@/features/play/components/AgentFeedback";
import {
  agentName,
  BUCKET_LABELS,
  bucketLabel,
  type HistoryFilter,
  type HistoryRow,
  historyDays,
  rowDetail,
  rowTitle,
  rowTotal,
  timeOf,
} from "./balance-history";
import { DetailLine, DrillRow, Group, Segmented } from "./parts";
import { useSubPage, useSubPageMove } from "./sub-page";

const FILTERS = [
  { value: "all", label: "All" },
  { value: "spent", label: "Spent" },
  { value: "added", label: "Added" },
] as const satisfies { value: HistoryFilter; label: string }[];

/** Darkest for the largest kind of credit, lighter for each smaller one. */
const SHADES = [
  "bg-foreground",
  "bg-foreground/60",
  "bg-foreground/35",
  "bg-foreground/20",
  "bg-foreground/10",
];

/** Older changes start loading this far before the foot of the history scrolls into view. */
const OLDER_PAGE_MARGIN = "0px 0px 240px 0px";

const clock = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const date = new Intl.DateTimeFormat(undefined, { month: "long", day: "numeric" });

type BalanceView =
  | { level: "history" }
  | { level: "run"; day: string; row: HistoryRow }
  | { level: "entry"; day: string; row: HistoryRow; line: CreditHistoryLine };

function amountTone(credits: number): string {
  return credits > 0 ? "text-foreground" : "text-muted-foreground";
}

/**
 * The balance in one place: how much there is, what it's made of, and how it changed, by day. A
 * run of AI calls in one game is one row; opening it lists each call, and opening a call or any
 * other row shows everything about it.
 */
export function BalancePage({ userId, onBack }: { userId: string; onBack: () => void }) {
  const [filter, setFilter] = useState<HistoryFilter>("all");
  const [view, setViewNow] = useState<BalanceView>({ level: "history" });
  const prepare = useSubPageMove();
  const setView = (next: BalanceView) => {
    prepare();
    setViewNow(next);
  };

  const title =
    view.level === "entry"
      ? (agentName(view.line) ?? journalLabel(view.line.journal))
      : view.level === "run"
        ? rowTitle(view.row)
        : "Balance";
  useSubPage(title, view.level === "entry" ? 3 : view.level === "run" ? 2 : 1, () => {
    if (view.level === "entry" && view.row.lines.length > 1) {
      setView({ level: "run", day: view.day, row: view.row });
    } else if (view.level === "history") {
      onBack();
    } else {
      setView({ level: "history" });
    }
  });

  if (view.level === "entry") return <EntryDetail line={view.line} day={view.day} />;

  if (view.level === "run") {
    const total = rowTotal(view.row);
    return (
      <div className="flex flex-col gap-4">
        <p className="px-1 text-xs text-muted-foreground tabular-nums">
          {view.day} · {view.row.lines.length} calls · {formatCredits(total, { signed: true })}
        </p>
        <Group>
          {view.row.lines.map(line => (
            <DrillRow
              key={line.entryId}
              title={agentName(line) ?? "AI usage"}
              detail={timeOf(line.recordedAt)}
              amount={formatCredits(line.credits, { signed: true })}
              amountClassName={amountTone(line.credits)}
              onOpen={() => setView({ level: "entry", day: view.day, row: view.row, line })}
            />
          ))}
        </Group>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Breakdown userId={userId} />
      <OpenHolds userId={userId} />
      <div className="mt-3 flex items-center justify-between gap-3 px-1">
        <p className="text-base font-medium">Balance history</p>
        <Segmented
          label="Show"
          options={FILTERS}
          value={filter}
          onChange={setFilter}
          className="w-52"
        />
      </div>
      <History
        userId={userId}
        filter={filter}
        onOpen={(day, row) => {
          const [only] = row.lines;
          setView(
            row.lines.length === 1 && only
              ? { level: "entry", day, row, line: only }
              : { level: "run", day, row },
          );
        }}
      />
    </div>
  );
}

/** When the daily credits next refill, at most a day off, or that the next sign-in refills them. */
function dailyNote(balance: BalanceResponse): string {
  const next = balance.refresh.nextClaimableAt ? new Date(balance.refresh.nextClaimableAt) : null;
  if (!next || next.getTime() <= Date.now()) return "Refills at next sign-in";
  const today = next.toDateString() === new Date().toDateString();
  return `Refills ${today ? "" : "tomorrow "}at ${clock.format(next)}`;
}

/**
 * What is left of the free allowance for direct AI calls outside games, which applies until the
 * player buys credits.
 */
function clientAllowanceNote(allowance: ClientAllowance): string {
  if (BigInt(allowance.remainingRaw) > 0n) {
    return `${formatCredits(creditsFromRaw(allowance.remainingRaw), { precise: true })} of free direct AI use left today`;
  }
  return allowance.resetsAt
    ? `Free direct AI use frees up at ${clock.format(new Date(allowance.resetsAt))}`
    : "Free direct AI use is spent for today";
}

/** What the balance is made of: a bar split by kind, and a row for each kind, largest first. */
function Breakdown({ userId }: { userId: string }) {
  const { data: balance } = useQuery(balanceQuery(userId));
  const { data: subscriptions } = useQuery(mySubscriptionQuery);
  if (!balance) {
    return (
      <div className="px-1">
        <Skeleton className="h-10 w-28" />
        <Skeleton className="mt-4 h-2 w-full rounded-full" />
      </div>
    );
  }

  const plan = subscriptions?.plan.current;
  const notes: Partial<Record<PlayerBucket, string>> = {
    daily: dailyNote(balance),
    ...(plan && {
      subscription: `${plan.cancelAtPeriodEnd ? "Ends" : "Renews"} ${date.format(new Date(plan.currentPeriodEnd))}`,
    }),
  };
  const kinds = (Object.keys(BUCKET_LABELS) as (keyof typeof BUCKET_LABELS)[])
    .map(bucket => ({ bucket, credits: creditsFromRaw(balance.buckets[bucket] ?? "0") }))
    .filter(kind => kind.credits > 0)
    .sort((a, b) => b.credits - a.credits);
  const total = kinds.reduce((sum, kind) => sum + kind.credits, 0);

  return (
    <div className="px-1">
      <p className="text-4xl font-semibold tracking-tight tabular-nums">
        {formatCredits(spendableCredits(balance))}
      </p>
      <p className="text-xs text-muted-foreground">Credits</p>
      {kinds.length > 0 ? (
        <>
          <div className="mt-4 flex h-2 gap-0.5 overflow-hidden rounded-full">
            {kinds.map((kind, index) => (
              <span
                key={kind.bucket}
                className={SHADES[Math.min(index, SHADES.length - 1)]}
                style={{ width: `${(kind.credits / total) * 100}%` }}
              />
            ))}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 @[40rem]/settings:grid-cols-2">
            {kinds.map((kind, index) => (
              <div key={kind.bucket} className="flex items-baseline gap-2 text-sm">
                <span
                  className={cn(
                    "size-2 shrink-0 translate-y-[-1px] rounded-full",
                    SHADES[Math.min(index, SHADES.length - 1)],
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{BUCKET_LABELS[kind.bucket]}</span>
                  {notes[kind.bucket] ? (
                    <span className="block truncate text-xs text-muted-foreground">
                      {notes[kind.bucket]}
                    </span>
                  ) : null}
                </span>
                <span className="tabular-nums">{formatCredits(kind.credits)}</span>
              </div>
            ))}
          </div>
        </>
      ) : null}
      {balance.clientAllowance.unlimited ? null : (
        <p className="mt-3 text-xs text-muted-foreground">
          {clientAllowanceNote(balance.clientAllowance)}
        </p>
      )}
    </div>
  );
}

/** The game, preset, or kind a hold was placed for. */
function holdContext(hold: OpenHold): string {
  return hold.gameTitle ?? hold.presetTitle ?? readableCode(hold.kind);
}

/** AI calls still running, and what each has set aside until it settles. */
function OpenHolds({ userId }: { userId: string }) {
  // Read each minute too, so a hold leaves once its deadline passes.
  const { data } = useQuery({ ...openHoldsQuery(userId), refetchInterval: 60_000 });
  const holds = data?.openHolds.filter(hold => new Date(hold.expiresAt) > new Date()) ?? [];
  if (holds.length === 0) return null;
  return (
    <Group label="In progress">
      {holds.map(hold => (
        <div key={hold.holdId} className={cn(panelRowQuiet, "h-auto min-h-11 gap-3 py-2")}>
          <span className="min-w-0 flex-1">
            <span className="block truncate">{holdContext(hold)}</span>
            <span className="block truncate text-xs text-muted-foreground">
              Started {timeOf(hold.startedAt)}
            </span>
          </span>
          <span className="shrink-0 text-muted-foreground tabular-nums">
            {formatCredits(creditsFromRaw(hold.estimateRaw), { precise: true })}
          </span>
        </div>
      ))}
    </Group>
  );
}

/** Watches the foot of the history, so the next older page loads as the reader scrolls to it. */
function useHistoryFoot(enabled: boolean, pageCount: number, onReach: () => void) {
  const foot = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = foot.current;
    if (!element || !enabled) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) onReach();
      },
      { rootMargin: OLDER_PAGE_MARGIN },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled, onReach, pageCount]);
  return foot;
}

function History({
  userId,
  filter,
  onOpen,
}: {
  userId: string;
  filter: HistoryFilter;
  onOpen: (day: string, row: HistoryRow) => void;
}) {
  const {
    data: lines,
    isPending,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery(creditHistoryQuery(userId));
  const loadOlder = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  const foot = useHistoryFoot(
    hasNextPage && !isFetchingNextPage && !isFetchNextPageError,
    lines?.length ?? 0,
    loadOlder,
  );

  if (isPending) {
    return (
      <Group>
        {[0, 1, 2].map(key => (
          <div key={key} className={cn(panelRowQuiet, "h-auto min-h-11 gap-3 py-2")}>
            <Skeleton className="h-4 w-36" />
          </div>
        ))}
      </Group>
    );
  }

  const days = historyDays(lines ?? [], filter);
  return (
    <>
      {days.length === 0 ? (
        <Group>
          <div className={cn(panelRowQuiet, "text-muted-foreground")}>
            <span className="min-w-0 flex-1 truncate">No changes yet</span>
          </div>
        </Group>
      ) : (
        days.map(group => (
          <Group key={group.day} label={group.day}>
            {group.rows.map(row => {
              const total = rowTotal(row);
              return (
                <DrillRow
                  key={row.key}
                  title={rowTitle(row)}
                  detail={rowDetail(row)}
                  amount={formatCredits(total, { signed: true })}
                  amountClassName={amountTone(total)}
                  onOpen={() => onOpen(group.day, row)}
                />
              );
            })}
          </Group>
        ))
      )}
      {lines?.length || hasNextPage ? (
        <p role="status" aria-live="polite" className="text-center text-xs text-muted-foreground">
          {isFetchingNextPage ? (
            "Loading earlier changes…"
          ) : isFetchNextPageError ? (
            <>
              Earlier changes failed to load.{" "}
              <button
                type="button"
                onClick={() => void fetchNextPage()}
                className="underline underline-offset-2 hover:text-foreground"
              >
                Retry
              </button>
            </>
          ) : hasNextPage ? (
            " "
          ) : (
            "Start of history"
          )}
        </p>
      ) : null}
      <div ref={foot} aria-hidden />
    </>
  );
}

/** Everything known about one change to the balance. */
function EntryDetail({ line, day }: { line: CreditHistoryLine; day: string }) {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const call = line.modelCall;
  const agent = call?.agent;
  const game = call?.game;
  return (
    <div className="flex flex-col gap-6">
      <div className="px-1">
        <p className="text-3xl font-semibold tracking-tight tabular-nums">
          {formatCredits(line.credits, { signed: true, precise: true })}
        </p>
        <p className="text-xs text-muted-foreground">
          {journalLabel(line.journal)} · {day}, {timeOf(line.recordedAt)}
        </p>
      </div>
      <Group>
        {game ? (
          <DetailLine label="Game">
            <Link
              to="/play/$gameId"
              params={{ gameId: game.id }}
              className="underline-offset-2 hover:underline"
            >
              {game.title}
            </Link>
          </DetailLine>
        ) : null}
        {agent ? <DetailLine label="Agent">{agentName(line)}</DetailLine> : null}
        {call ? (
          <DetailLine label="Shared with">
            {call.payerCount === 1
              ? "Nobody"
              : `${call.payerCount - 1} other ${call.payerCount === 2 ? "player" : "players"}`}
          </DetailLine>
        ) : null}
        {line.buckets.map(({ bucket, credits }) => (
          <DetailLine key={bucket} label={credits < 0 ? "From" : "Into"}>
            {bucketLabel(bucket)} ·{" "}
            <span className="tabular-nums">
              {formatCredits(Math.abs(credits), { precise: true })}
            </span>
          </DetailLine>
        ))}
        <DetailLine label="Reference">
          <span className="font-mono text-xs">{call?.callId ?? line.entryId}</span>
        </DetailLine>
      </Group>
      {agent && game ? (
        <Group>
          <button type="button" onClick={() => setFeedbackOpen(true)} className={panelRow}>
            <Flag aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate text-left">Give feedback on {agent.name}</span>
            <HapticSurface />
          </button>
          <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{agentName(line)}</DialogTitle>
                <DialogDescription>In {game.title}</DialogDescription>
              </DialogHeader>
              <AgentFeedback
                gameId={game.id}
                versionId={agent.versionId}
                question={`How is ${agent.name} doing?`}
              />
            </DialogContent>
          </Dialog>
        </Group>
      ) : null}
    </div>
  );
}
